import { useEffect, useState } from 'react';

import { fetchStaffMember } from '../../../../api/staff';
import { actionColor, actionLabel, toAuditRow, type AuditRow } from '../auditViewModel';

interface UserDetailRecentActivityProps {
  userId: string;
  userName: string;
}

const MAX_EVENTS = 5;

/** Compact "Recent activity" subsection embedded in the user detail pane.
 *  Lazy-loads the per-user audit via fetchStaffMember(:id).audit each time
 *  the selected user changes. Renders the most recent `MAX_EVENTS` events. */
export default function UserDetailRecentActivity({
  userId,
  userName,
}: UserDetailRecentActivityProps) {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    fetchStaffMember(userId)
      .then((detail) => {
        if (cancelled) return;
        const audit = detail.audit ?? [];
        const mapped = audit
          .map((e) => toAuditRow(detail.name || userName, e))
          .sort((a, b) => b.changedAt.getTime() - a.changedAt.getTime())
          .slice(0, MAX_EVENTS);
        setRows(mapped);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load activity');
      });
    return () => {
      cancelled = true;
    };
  }, [userId, userName]);

  return (
    <div className="px-[18px] pb-3.5">
      <div className="text-[10px] font-semibold tracking-[.08em] uppercase text-stone-500 dark:text-stone-400 mb-2">
        Recent activity
      </div>
      {rows === null && !error && (
        <div className="text-[11.5px] text-stone-500 dark:text-stone-400">Loading…</div>
      )}
      {error && <div className="text-[11.5px] text-clay-700">{error}</div>}
      {rows && rows.length === 0 && (
        <div className="text-[11.5px] text-stone-500 dark:text-stone-400">
          No events recorded yet.
        </div>
      )}
      {rows && rows.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {rows.map((e) => (
            <ActivityRow key={e.id} row={e} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActivityRow({ row }: { row: AuditRow }) {
  const before = row.before === null || row.before === undefined ? null : String(row.before);
  const after = row.after === null || row.after === undefined ? null : String(row.after);
  const showRoleDiff = row.action === 'role-changed' && before && after;
  return (
    <div className="flex items-start gap-2 px-2.5 py-2 rounded-lg bg-stone-50 dark:bg-[#1e2520] text-[11.5px]">
      <i
        className="mt-1 w-1.5 h-1.5 rounded-full flex-none"
        style={{ background: actionColor(row.action) }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-bark-900 dark:text-[#e8ece9] font-medium">
          {actionLabel(row.action)}
          {showRoleDiff && (
            <span className="text-stone-500 dark:text-stone-400 font-normal">
              {' · '}
              {before} → <strong className="text-bark-800 dark:text-[#e8ece9]">{after}</strong>
            </span>
          )}
        </div>
        <div className="text-stone-500 dark:text-stone-400 text-[10.5px] mt-0.5">
          {row.ts} · by {row.actor}
        </div>
      </div>
    </div>
  );
}
